/**
 * UnusedDiscountBanner — stage "banner" of the hybrid activation nudge for a
 * free account that signed up with an attached, still-unconsumed incentive
 * (a starter credit promise and/or founding-discount eligibility — see
 * lib/activation-incentive.ts).
 *
 * Mounted ONCE by `ProLayout` (mirrors NoStandingOffersBanner's mount point)
 * on every authed page EXCEPT `/pricing` (the CTA's own destination) and
 * `/welcome` (the WelcomeDiscountModal owns that moment).
 *
 * Hybrid semantics (operator decision 2026-08-12): this banner shows until
 * the pro dismisses it ONCE — the dismissal is durable and account-keyed,
 * NOT per-session — after which the compact header `DiscountChip` takes over
 * permanently as the ambient reminder. All visibility gating (incentive
 * present, rollout flag, ActivationBanner suppression, preview `?discount=`
 * seam) lives in `useDiscountNudge`, shared with the chip so the two
 * surfaces can never disagree; exactly one of them renders at a time.
 */
import { Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import {
  dismissDiscountBanner,
  useDiscountNudge,
  useNudgeShownEvent,
} from "../../hooks/useDiscountNudge";

export function UnusedDiscountBanner() {
  const nudge = useDiscountNudge();
  const posthog = usePostHog();

  const visible = nudge?.stage === "banner";
  const variant = visible ? nudge.incentive.variant : null;
  useNudgeShownEvent("crm_billing.discount_banner_shown", variant);

  if (!visible) return null;
  const { incentive } = nudge;

  function handleDismiss() {
    // Durable, account-keyed — flips the shared nudge stage to "chip" for
    // every mounted surface immediately (see useDiscountNudge).
    dismissDiscountBanner();
    posthog?.capture("crm_billing.discount_banner_dismissed", {
      variant: incentive.variant,
    });
  }

  function handleCtaClick() {
    posthog?.capture("crm_billing.discount_banner_cta_clicked", {
      variant: incentive.variant,
    });
  }

  return (
    <div
      role="status"
      data-testid="unused-discount-banner"
      className="flex items-center gap-3 border-b border-primary/20 bg-primary/5 px-4 py-3 text-sm sm:px-8"
    >
      <Sparkles
        aria-hidden="true"
        className="h-4 w-4 flex-shrink-0 text-primary"
      />
      <p className="min-w-0 flex-1 text-fg">
        {incentive.bannerLine}{" "}
        <Link
          to="/pricing"
          onClick={handleCtaClick}
          className="whitespace-nowrap font-semibold text-primary underline underline-offset-2"
        >
          Activate Sill Pro
        </Link>
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 rounded-md p-1 text-fg-muted transition hover:bg-primary/10 hover:text-fg"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
